import React, { useState, useEffect, useContext, useRef, useCallback } from "react";

// Section for Utilities and Context
import "./publicsquare.css";
import API from "../utils/API";
import { AuthContext } from "../utils/AuthContext";

// Section for Components
import PostCard from "../components/PostCard";
import HeadlineFeed from "../components/HeadlineFeed";
import CreatePost from "../components/CreatePost";
import PostFilters from "../components/PostFilters";
import LoginModal from "../components/LoginModal";
import SEO from "../components/SEO";
// import AdComponent from "../components/AdComponent";

export default function PublicSquare() {
    const { auth } = useContext(AuthContext); // Removed setAuth if unused
    const hasFetchedOnceRef = useRef(false);

    const [allPosts, setAllPosts] = useState([]);
    const [userLikes, setUserLikes] = useState([]);
    const [searchQuery, setSearchQuery] = useState("");

    const [postTypeSelect, setPostTypeSelect] = useState(undefined);
    const [postTypesList, setPostTypesList] = useState([]);

    const [sortMode, setSortMode] = useState('recent');

    const [isFetching, setIsFetching] = useState(false);
    const [hasMore, setHasMore] = useState(true);

    const [activeSection, setActiveSection] = useState('posts');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const limit = 40;

    // Refs for values that must not recreate fetchPosts when they change
    const offsetRef = useRef(0);
    const isFetchingRef = useRef(false);
    const observer = useRef();

    // --- FETCH FUNCTION ---
    // offset is a ref so changing it never recreates this callback, breaking the cascade loop
    const fetchPosts = useCallback(async (isReset = false) => {
        if (isFetchingRef.current && !isReset) return;
        isFetchingRef.current = true;
        setIsFetching(true);

        const currentOffset = isReset ? 0 : offsetRef.current;

        try {
            const response = await API.getPosts(currentOffset, limit, sortMode, postTypeSelect, debouncedSearch);
            const newPosts = response.data;

            if (isReset) {
                setAllPosts(newPosts);
            } else {
                setAllPosts(prev => [...prev, ...newPosts.filter(p => !prev.find(ep => ep.post_id === p.post_id))]);
            }
            offsetRef.current = currentOffset + newPosts.length;
            setHasMore(!debouncedSearch && newPosts.length === limit);
        } catch (error) {
            console.error("Error fetching post info:", error);
        } finally {
            isFetchingRef.current = false;
            setIsFetching(false);
        }
    }, [sortMode, postTypeSelect, limit, debouncedSearch]);

    // --- EFFECT: LISTEN FOR FILTER / SEARCH CHANGES ---
    // fetchPosts intentionally omitted — it only changes when sort/type/search change,
    // which are already listed here, so including it would cause a double-fire.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        if (hasFetchedOnceRef.current) {
            fetchPosts(true);
        }
    }, [sortMode, postTypeSelect, debouncedSearch]);

    // --- SEARCH SUBMIT ---
    // debouncedSearch is what fetchPosts reads; setting it here triggers the effect above.
    const handleSearchSubmit = useCallback(() => {
        setDebouncedSearch(searchQuery.trim());
    }, [searchQuery]);

    // --- INITIAL LOAD ---
    useEffect(() => {
        fetchPosts(true);
        hasFetchedOnceRef.current = true;

        const fetchPostTypes = async () => {
            try {
                const data = await API.getAllPostTypes();
                setPostTypesList(data.data);
            } catch (error) { console.log(error); }
        }
        fetchPostTypes();

        const fetchUserLikes = async () => {
            if (!auth.user) return;
            try {
                const data = await API.getUserLikes(auth.user.user_id);
                setUserLikes(data.data);
            } catch (error) { console.log(error); }
        }
        fetchUserLikes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // --- INFINITE SCROLL OBSERVER ---
    const lastPostElementRef = useCallback(node => {
        if (isFetching) return;
        if (observer.current) observer.current.disconnect();

        observer.current = new IntersectionObserver(entries => {
            if (entries[0].isIntersecting && hasMore) {
                fetchPosts(false);
            }
        });

        if (node) observer.current.observe(node);
    }, [isFetching, hasMore, fetchPosts]);

    // --- RESIZE HANDLER ---
    useEffect(() => {
        const handleResize = () => {
            if (window.innerWidth >= 1200) {
                setActiveSection(null);
            } else {
                setActiveSection(prev => prev === null ? 'posts' : prev);
            }
        };
        window.addEventListener('resize', handleResize);
        handleResize();
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // --- REFRESH FUNCTION ---
    const refreshPosts = useCallback(() => fetchPosts(true), [fetchPosts]);

    // --- FILTER HANDLERS ---
    const handleResetFilters = () => {
        setSearchQuery("");
        setDebouncedSearch("");
        setPostTypeSelect(undefined);
        setSortMode('recent');
    };
    
    // --- RENDER HELPER ---
    // We create a helper to render the list to avoid duplicating code in Mobile/Desktop views
    const renderPostList = () => {
        if (isFetching && allPosts.length === 0) {
            return (
                <div className="d-flex position-fixed top-50 start-50">
                    <div className="spinner-border loading-spinner" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                </div>
            );
        }

        return allPosts.map((post, index) => {
            const isLastPost = allPosts.length === index + 1;
            return (
                <div ref={isLastPost ? lastPostElementRef : null} key={post.post_id} className="col-12 p-0 mb-0">
                    <PostCard
                        post_id={post.post_id}
                        title={post.post_title}
                        author={post.ProfileInfo ? `${post.ProfileInfo.first_name} ${post.ProfileInfo.last_name}` : "Anonymous"}
                        author_id={post.post_author}
                        post_type={post.post_type}
                        postTypeName={postTypesList.find(t => t.posttype_id === post.post_type)?.post_type ?? ''}
                        userLikes={userLikes}
                        body={post.post_body}
                        org_id={post.organization_id}
                        div_id={post.division_id}
                        postDate={post.created_at}
                        likes={post.likes_count}
                        reads={post.reads}
                        comments={post.PostComments}
                        user_id={post.ProfileInfo?.user_id}
                        event_location={post.event_location}
                        event_date={post.event_date}
                        event_start={post.event_start}
                        event_end={post.event_end}
                        event_type={post.event_type}
                        refresh={refreshPosts}
                    />
                </div>
            );
        });
    };

    return (
        <>
           <SEO
                title="Public Square | 'elloGovna"
                description="Browse announcements, press events, and curated policy headlines shared across California's Capitol community on 'elloGovna."
                path="/public-square"
           />
           <div className="row public-square-row">
                <div className="mobile-toggle-buttons d-block d-xl-none mb-3">
                    <div className="mobile-toggle-div">
                        <button 
                            className={`${activeSection === 'posts' ? 'selected-mobile-feed' : 'nonselected-mobile-feed'}`} 
                            onClick={() => setActiveSection('posts')}
                        >
                            Posts
                        </button>
                        <button 
                            className={`${activeSection === 'headlines' ? 'selected-mobile-feed' : 'nonselected-mobile-feed'}`} 
                            onClick={() => setActiveSection('headlines')}
                        >
                            Headlines
                        </button>
                    </div>
                </div>

                {/* Mobile View: Posts */}
                {activeSection === 'posts' && (
                    <>
                        <PostFilters
                            searchQuery={searchQuery}
                            setSearchQuery={setSearchQuery}
                            onSearchSubmit={handleSearchSubmit}
                            sortMode={sortMode}
                            setSortMode={setSortMode}
                            postTypeSelect={postTypeSelect}
                            setPostTypeSelect={setPostTypeSelect}
                            postTypesList={postTypesList}
                            handleResetFilters={handleResetFilters}
                        />
                        <div className="col-md-10 col-sm-12 public-square-middle-column">                    
                            <div className="row posts-feed mt-4">
                                {renderPostList()}
                            </div>
                        </div>
                    </>
                )}

                {/* Desktop View */}
                {activeSection === null && (
                    <>
                        <PostFilters
                            searchQuery={searchQuery}
                            setSearchQuery={setSearchQuery}
                            onSearchSubmit={handleSearchSubmit}
                            sortMode={sortMode}
                            setSortMode={setSortMode}
                            postTypeSelect={postTypeSelect}
                            setPostTypeSelect={setPostTypeSelect}
                            postTypesList={postTypesList}
                            handleResetFilters={handleResetFilters}
                        />
                        <div className="col-xl-4 col-lg-5 col-md-6 col-sm-12 public-square-middle-column">                    
                            <div className="row posts-feed mt-4">
                                {renderPostList()}
                            </div>
                        </div>
                        <div className="col-xl-2 col-lg-3 col-md-5 col-sm-12 public-square-right-column">
                            <HeadlineFeed />
                        </div>
                    </>
                )}
                
                {/* Mobile View: Headlines */}
                {(activeSection === 'headlines') && (
                    <div className="col-md-10 col-sm-12 public-square-right-column">
                        <HeadlineFeed />
                    </div>
                )}
            </div>
            <div className="row last-row"/>
            <CreatePost
                refresh={refreshPosts}
            />
            <LoginModal />

        </>
    )
}